import i18n from 'i18next';
import { initReactI18next } from 'react-i18next';

import ar from './locales/ar.json';
import en from './locales/en.json';
import {
  applyDocumentLocale,
  DEFAULT_LOCALE,
  LOCALE_STORAGE_KEY,
  normaliseLocale,
  SUPPORTED_LOCALES,
  type SupportedLocale,
} from './direction';

/**
 * Translation resources are bundled rather than fetched.
 *
 * Two small files beat a network round trip on a clinic's connection, and it removes a class of
 * bug where the interface renders before its words arrive (NFR-010).
 */
export const resources = {
  ar: { translation: ar },
  en: { translation: en },
} as const;

/** The language to start in: the reader's last choice, then the browser's, then Arabic. */
export function resolveInitialLocale(storage: Storage | undefined = globalThis.localStorage): SupportedLocale {
  const stored = storage?.getItem(LOCALE_STORAGE_KEY);
  if (stored) {
    return normaliseLocale(stored);
  }
  return normaliseLocale(globalThis.navigator?.language);
}

export function initialiseI18n(locale: SupportedLocale = resolveInitialLocale()) {
  if (!i18n.isInitialized) {
    void i18n.use(initReactI18next).init({
      resources,
      lng: locale,
      fallbackLng: DEFAULT_LOCALE,
      supportedLngs: [...SUPPORTED_LOCALES],
      interpolation: { escapeValue: false },
      returnNull: false,
      // A key with no translation used to be printed as itself, so a value the
      // API added and the catalogue had not caught up with — a new expense
      // category, a new refund status — reached a clinic's screen as the literal
      // string `expenses.statuses.PENDING`. That is worse than a rough word:
      // it looks like the software is broken rather than merely behind.
      //
      // The last segment, humanised, is always at least readable, and because
      // the catalogue's leaf keys are the API's own enum values it is usually
      // exactly right. Anything that matters more than that passes an explicit
      // default, which i18next uses in preference to this.
      parseMissingKeyHandler: humaniseMissingKey,
    });
  }
  applyDocumentLocale(locale);
  return i18n;
}

/**
 * `expenses.statuses.PARTIALLY_REFUNDED` → `Partially refunded`.
 *
 * Deliberately dumb: it takes the last dotted segment, splits on underscores and case changes, and
 * sentence-cases the result. It is a safety net for a key nobody translated, not a translation —
 * it produces English in an Arabic interface, which is the correct trade against printing a dotted
 * identifier in either language.
 */
export function humaniseMissingKey(key: string): string {
  const leaf = key.split('.').pop() ?? key;
  const words = leaf
    .replace(/[_-]+/g, ' ')
    .replace(/([a-z\d])([A-Z])/g, '$1 $2')
    .trim()
    .toLowerCase();
  return words.charAt(0).toUpperCase() + words.slice(1);
}

/** Switches language, mirrors the document, and remembers the choice. */
export async function changeLocale(
  locale: SupportedLocale,
  storage: Storage | undefined = globalThis.localStorage,
): Promise<void> {
  await i18n.changeLanguage(locale);
  applyDocumentLocale(locale);
  storage?.setItem(LOCALE_STORAGE_KEY, locale);
}

export default i18n;
